import { AddressAutocompleteInput } from '../common/AddressAutocompleteInput';
import React, { useState, useEffect, useRef } from 'react';
import { PaymentStep, type PaymentStepHandle } from './PaymentStep';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  FormControl,
  FormLabel,
  Input,
  Select,
  Option,
  Alert,
  Stepper,
  Step,
  StepIndicator,
  Stack,
  CircularProgress,
  Chip,
  Checkbox,
} from '@mui/joy';
import Grid from '@mui/joy/Grid';
import { Check } from '@phosphor-icons/react';
import { api } from '../../lib/api';
import { extractReferralFromURL, isValidReferralCode } from '../../utils/affiliate';
import { WebsiteInput, type WebsiteProtocol } from '../common/WebsiteInput';
import { LEGAL_ENTITY } from '../../pages/legal/legalContent';

interface SignupFormData {
  // Personal info
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  password: string;
  passwordConfirm: string;
  
  // Company info
  companyName: string;
  companyPhone: string;
  companyEmail: string;
  mainOfficeAddress1: string;
  mainOfficeAddress2: string;
  mainOfficeCity: string;
  mainOfficeState: string;
  mainOfficeZip: string;
  websiteProtocol: 'http://' | 'https://';
  websiteDomain: string;
  residentialCommercialBoth: 'residential' | 'commercial' | 'both';
  yearsInBusiness: number;
  targetCustomers: string;
  
  // Referral info
  referralCode: string;

  // Consent
  smsConsent: boolean;
  termsConsent: boolean;

  // Stripe payment method (set on Step 2 after card confirmation)
  paymentMethodId: string | null;
}

export const SignupForm: React.FC = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [referralValidation, setReferralValidation] = useState<{
    isValid: boolean | null;
    affiliateName: string;
    message: string;
  }>({ isValid: null, affiliateName: '', message: '' });
  const [formData, setFormData] = useState<SignupFormData>({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    password: '',
    passwordConfirm: '',
    companyName: '',
    companyPhone: '',
    companyEmail: '',
    mainOfficeAddress1: '',
    mainOfficeAddress2: '',
    mainOfficeCity: '',
    mainOfficeState: '',
    mainOfficeZip: '',
    websiteProtocol: 'https://',
    websiteDomain: '',
    residentialCommercialBoth: 'both',
    yearsInBusiness: 0,
    targetCustomers: '',
    referralCode: '',
    smsConsent: false,
    termsConsent: false,
    paymentMethodId: null,
  });

  const paymentStepRef = useRef<PaymentStepHandle>(null);

  // Extract referral code from URL on component mount
  useEffect(() => {
    const urlReferralCode = extractReferralFromURL();
    if (urlReferralCode) {
      setFormData(prev => ({ ...prev, referralCode: urlReferralCode }));
      validateReferralCode(urlReferralCode);
    }
  }, []);


  const handleInputChange = (field: keyof SignupFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Validate referral code when it changes
    if (field === 'referralCode') {
      validateReferralCode(value);
    }
  };

  const validateReferralCode = async (code: string) => {
    if (!code || !isValidReferralCode(code)) {
      setReferralValidation({
        isValid: code ? false : null,
        affiliateName: '',
        message: code ? 'Invalid referral code format' : ''
      });
      return;
    }

    try {
      const response = await api.post<{
        valid: boolean;
        affiliate_name?: string;
        message?: string;
      }>('/api/affiliates/signup/validate_referral_code/', {
        referral_code: code
      });
      
      setReferralValidation({
        isValid: response.data.valid,
        affiliateName: response.data.affiliate_name || '',
        message: response.data.message || ''
      });
    } catch (error) {
      setReferralValidation({
        isValid: false,
        affiliateName: '',
        message: 'Unable to validate referral code'
      });
    }
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 0: // Personal info
        return !!(
          formData.firstName &&
          formData.lastName &&
          formData.email &&
          formData.phone &&
          formData.password &&
          formData.password === formData.passwordConfirm &&
          formData.password.length >= 8
        );
      case 1: // Company info
        return !!(
          formData.companyName &&
          formData.companyPhone &&
          formData.companyEmail &&
          formData.mainOfficeAddress1 &&
          formData.mainOfficeCity &&
          formData.mainOfficeState &&
          formData.mainOfficeZip &&
          formData.yearsInBusiness > 0 &&
          formData.targetCustomers &&
          formData.termsConsent
        );
      case 2: // Payment — actual validation happens via Stripe.confirmSetup
        return true;
      default:
        return false;
    }
  };

  const getValidationErrors = (step: number): string[] => {
    const errors: string[] = [];
    
    switch (step) {
      case 0: // Personal info
        if (!formData.firstName) errors.push('First Name');
        if (!formData.lastName) errors.push('Last Name');
        if (!formData.email) errors.push('Email');
        if (!formData.phone) errors.push('Phone');
        if (!formData.password) errors.push('Password');
        if (formData.password && formData.password.length < 8) errors.push('Password (minimum 8 characters)');
        if (formData.password && formData.passwordConfirm && formData.password !== formData.passwordConfirm) errors.push('Password confirmation (must match password)');
        if (!formData.passwordConfirm) errors.push('Password confirmation');
        break;
      case 1: // Company info
        if (!formData.companyName) errors.push('Company Name');
        if (!formData.companyPhone) errors.push('Company Phone');
        if (!formData.companyEmail) errors.push('Company Email');
        if (!formData.mainOfficeAddress1) errors.push('Main Office Address');
        if (!formData.mainOfficeCity) errors.push('City');
        if (!formData.mainOfficeState) errors.push('State');
        if (!formData.mainOfficeZip) errors.push('ZIP');
        if (!formData.yearsInBusiness || formData.yearsInBusiness <= 0) errors.push('Years in Business');
        if (!formData.targetCustomers) errors.push('Target Customers');
        if (!formData.termsConsent) errors.push('Agreement to Terms of Use and Privacy Policy');
        break;
    }
    
    return errors;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep(prev => prev + 1);
      setError(null);
    } else {
      const errors = getValidationErrors(activeStep);
      if (errors.length === 1) {
        setError(`Please fill in: ${errors[0]}`);
      } else {
        setError(`Please fill in the following required fields: ${errors.join(', ')}`);
      }
    }
  };

  const handleBack = () => {
    setActiveStep(prev => prev - 1);
    setError(null);
  };

  const handleSubmit = async () => {
    // Validate the current step before submitting
    if (!validateStep(activeStep)) {
      const errors = getValidationErrors(activeStep);
      if (errors.length === 1) {
        setError(`Please fill in: ${errors[0]}`);
      } else {
        setError(`Please fill in the following required fields: ${errors.join(', ')}`);
      }
      return;
    }

    setLoading(true);
    setError(null);

    // Step 2 (Payment): confirm the SetupIntent with Stripe to get a
    // payment_method ID before POSTing to /api/signup/.
    let paymentMethodId = formData.paymentMethodId;
    if (!paymentMethodId) {
      if (!paymentStepRef.current) {
        setError('Payment form is not ready. Please wait a moment.');
        setLoading(false);
        return;
      }
      paymentMethodId = await paymentStepRef.current.confirm();
      if (!paymentMethodId) {
        // Inline error already shown by PaymentStep
        setLoading(false);
        return;
      }
      setFormData((prev) => ({ ...prev, paymentMethodId }));
    }

    try {
      // Create organization and user with a 7-day trial subscription.
      const website = formData.websiteDomain ?
        `${formData.websiteProtocol}${formData.websiteDomain}` :
        '';

      // Use fetch directly for signup to avoid adding Authorization header
      const response = await fetch('/api/signup/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          first_name: formData.firstName,
          last_name: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          password: formData.password,
          password_confirm: formData.passwordConfirm,
          company_name: formData.companyName,
          company_phone_number: formData.companyPhone,
          company_email: formData.companyEmail,
          main_office_address_1: formData.mainOfficeAddress1,
          main_office_address_2: formData.mainOfficeAddress2,
          main_office_city: formData.mainOfficeCity,
          main_office_state: formData.mainOfficeState,
          main_office_zip: formData.mainOfficeZip,
          website: website,
          residential_commercial_both: formData.residentialCommercialBoth,
          years_in_business: formData.yearsInBusiness,
          target_customers: formData.targetCustomers,
          referral_code: formData.referralCode || '',
          sms_consent: formData.smsConsent,
          terms_accepted: formData.termsConsent,
          payment_method_id: paymentMethodId,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Handle validation errors from backend
        if (errorData.errors) {
          const errors = errorData.errors;
          if (errors.non_field_errors) {
            const errorMsg = Array.isArray(errors.non_field_errors) 
              ? errors.non_field_errors[0] 
              : errors.non_field_errors;
            throw { type: 'validation', errors, message: errorMsg };
          } else {
            // Get the first field error
            const firstErrorKey = Object.keys(errors)[0];
            const firstError = errors[firstErrorKey];
            const errorMsg = Array.isArray(firstError) ? firstError[0] : firstError;
            throw { type: 'validation', errors, message: errorMsg };
          }
        }
        
        // Handle other error formats
        throw { type: 'api', message: errorData.error || errorData.detail || 'Signup failed' };
      }

      const signupResponse = await response.json() as {
        message: string;
        email: string;
        organization_id: string;
      };

      // Redirect to success page - email verification required
      // Pass email as query param so user can resend verification
      window.location.href = `/signup/success?email=${encodeURIComponent(signupResponse.email || formData.email)}`;
    } catch (err: any) {
      console.error('Signup error:', err);
      
      // Handle validation errors
      if (err.type === 'validation') {
        setError(err.message);
      } else if (err.type === 'api') {
        setError(err.message);
      } else {
        // Fallback for unexpected error formats
        setError(err.message || 'Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const steps = ['Personal Info', 'Company Info', 'Payment'];

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', p: { xs: 2, md: 3 } }}>
      <Card>
        <CardContent>
          <Typography level="h2" sx={{ mb: 4, textAlign: 'center' }}>
            Create Your ApexOS Account
          </Typography>

          <Box sx={{ display: { xs: 'block', sm: 'none' }, mb: 3, textAlign: 'center' }}>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              Step {activeStep + 1} of {steps.length}
            </Typography>
            <Typography level="title-md" color="primary">
              {steps[activeStep]}
            </Typography>
          </Box>

          <Stepper sx={{ mb: 4, display: { xs: 'none', sm: 'flex' } }}>
            {steps.map((step, index) => (
              <Step
                key={step}
                indicator={
                  <StepIndicator
                    variant={activeStep > index ? 'solid' : activeStep === index ? 'outlined' : 'plain'}
                    color={activeStep >= index ? 'primary' : 'neutral'}
                  >
                    {activeStep > index ? <Check /> : index + 1}
                  </StepIndicator>
                }
              >
                <Typography
                  level="title-sm"
                  color={activeStep >= index ? 'primary' : 'neutral'}
                >
                  {step}
                </Typography>
              </Step>
            ))}
          </Stepper>


          {error && (
            <Alert color="danger" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Step 0: Personal Info */}
          {activeStep === 0 && (
            <Stack spacing={3}>
              <Grid container spacing={2}>
                <Grid xs={12} sm={6}>
                  <FormControl required>
                    <FormLabel>First Name</FormLabel>
                    <Input
                      value={formData.firstName}
                      onChange={(e) => handleInputChange('firstName', e.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl required>
                    <FormLabel>Last Name</FormLabel>
                    <Input
                      value={formData.lastName}
                      onChange={(e) => handleInputChange('lastName', e.target.value)}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl required>
                <FormLabel>Email</FormLabel>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </FormControl>

              <FormControl required>
                <FormLabel>Phone</FormLabel>
                <Input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                />
              </FormControl>

              <Grid container spacing={2}>
                <Grid xs={12} sm={6}>
                  <FormControl required>
                    <FormLabel>Password</FormLabel>
                    <Input
                      type="password"
                      value={formData.password}
                      onChange={(e) => handleInputChange('password', e.target.value)}
                      placeholder="Minimum 8 characters"
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl required>
                    <FormLabel>Confirm Password</FormLabel>
                    <Input
                      type="password"
                      value={formData.passwordConfirm}
                      onChange={(e) => handleInputChange('passwordConfirm', e.target.value)}
                      error={!!formData.passwordConfirm && formData.password !== formData.passwordConfirm}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <Box>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                  <FormLabel>Referral Code</FormLabel>
                  <Chip size="sm" variant="soft" color="success">Optional</Chip>
                </Box>
                <FormControl>
                  <Input
                    value={formData.referralCode}
                    onChange={(e) => handleInputChange('referralCode', e.target.value)}
                    placeholder="Enter referral code from your affiliate"
                  />
                </FormControl>
                {referralValidation.message && (
                  <Typography 
                    level="body-xs" 
                    color={referralValidation.isValid === true ? 'success' : 'danger'}
                    sx={{ mt: 0.5 }}
                  >
                    {referralValidation.isValid === true && referralValidation.affiliateName && 
                      `✓ Valid referral code from ${referralValidation.affiliateName}`
                    }
                    {referralValidation.isValid === false && `✗ ${referralValidation.message}`}
                  </Typography>
                )}
              </Box>

              <Box>
                <Checkbox
                  checked={formData.smsConsent}
                  onChange={(e) => handleInputChange('smsConsent', e.target.checked)}
                  label="I agree to receive SMS notifications from ApexOS regarding account activity and system alerts. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe or HELP for assistance."
                  sx={{ alignItems: 'flex-start' }}
                />
                {/* A2P 10DLC/CTIA: policy links must remain visible at the point of SMS opt-in */}
                <Typography level="body-xs" sx={{ mt: 0.75, color: 'text.tertiary' }}>
                  SMS consent is optional and not required to create an account. See our{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--brand-text, #7013f5)' }}
                  >
                    Privacy Policy
                  </a>{' '}
                  and{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--brand-text, #7013f5)' }}
                  >
                    Terms of Use
                  </a>{' '}
                  for details about our SMS notifications program.
                </Typography>
              </Box>
            </Stack>
          )}

          {/* Step 1: Company Info */}
          {activeStep === 1 && (
            <Stack spacing={3}>
              <FormControl required>
                <FormLabel>Company Name</FormLabel>
                <Input
                  value={formData.companyName}
                  onChange={(e) => handleInputChange('companyName', e.target.value)}
                />
              </FormControl>

              <Grid container spacing={2}>
                <Grid xs={12} sm={6}>
                  <FormControl required>
                    <FormLabel>Company Phone</FormLabel>
                    <Input
                      type="tel"
                      value={formData.companyPhone}
                      onChange={(e) => handleInputChange('companyPhone', e.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl required>
                    <FormLabel>Company Email</FormLabel>
                    <Input
                      type="email"
                      value={formData.companyEmail}
                      onChange={(e) => handleInputChange('companyEmail', e.target.value)}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl required>
                <FormLabel>Main Office Address</FormLabel>
                <AddressAutocompleteInput
                  value={formData.mainOfficeAddress1}
                  onChange={(value) => handleInputChange('mainOfficeAddress1', value)}
                  onAddressSelect={(parsed) => setFormData(previous => ({ ...previous, mainOfficeAddress1: parsed.address_1, mainOfficeCity: parsed.city, mainOfficeState: parsed.state, mainOfficeZip: parsed.zip_code }))}
                />
              </FormControl>

              <FormControl>
                <FormLabel>Address Line 2</FormLabel>
                <Input
                  value={formData.mainOfficeAddress2}
                  onChange={(e) => handleInputChange('mainOfficeAddress2', e.target.value)}
                />
              </FormControl>

              <Grid container spacing={2}>
                <Grid xs={12} sm={5}>
                  <FormControl required>
                    <FormLabel>City</FormLabel>
                    <Input
                      value={formData.mainOfficeCity}
                      onChange={(e) => handleInputChange('mainOfficeCity', e.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={4}>
                  <FormControl required>
                    <FormLabel>State</FormLabel>
                    <Input
                      value={formData.mainOfficeState}
                      onChange={(e) => handleInputChange('mainOfficeState', e.target.value)}
                    />
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={3}>
                  <FormControl required>
                    <FormLabel>ZIP</FormLabel>
                    <Input
                      value={formData.mainOfficeZip}
                      onChange={(e) => handleInputChange('mainOfficeZip', e.target.value)}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <WebsiteInput
                protocol={formData.websiteProtocol as WebsiteProtocol}
                domain={formData.websiteDomain}
                onProtocolChange={(value) => handleInputChange('websiteProtocol', value)}
                onDomainChange={(value) => handleInputChange('websiteDomain', value)}
              />

              <Grid container spacing={2}>
                <Grid xs={12} sm={6}>
                  <FormControl required>
                    <FormLabel>Business Type</FormLabel>
                    <Select
                      value={formData.residentialCommercialBoth}
                      onChange={(_, value) => handleInputChange('residentialCommercialBoth', value)}
                    >
                      <Option value="residential">Residential</Option>
                      <Option value="commercial">Commercial</Option>
                      <Option value="both">Both</Option>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid xs={12} sm={6}>
                  <FormControl required>
                    <FormLabel>Years in Business</FormLabel>
                    <Input
                      type="number"
                      value={formData.yearsInBusiness}
                      onChange={(e) => handleInputChange('yearsInBusiness', parseInt(e.target.value) || 0)}
                    />
                  </FormControl>
                </Grid>
              </Grid>

              <FormControl required>
                <FormLabel>Target Customers</FormLabel>
                <Input
                  value={formData.targetCustomers}
                  onChange={(e) => handleInputChange('targetCustomers', e.target.value)}
                  placeholder="e.g., Homeowners, Property managers, etc."
                />
              </FormControl>

              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Checkbox
                  checked={formData.termsConsent}
                  onChange={(e) => handleInputChange('termsConsent', e.target.checked)}
                />
                <Typography level="body-sm">
                  I agree to the{' '}
                  <a
                    href="/terms"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--brand-text, #7013f5)' }}
                  >
                    Terms of Use
                  </a>{' '}
                  and{' '}
                  <a
                    href="/privacy"
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{ color: 'var(--brand-text, #7013f5)' }}
                  >
                    Privacy Policy
                  </a>
                  .
                </Typography>
              </Box>
            </Stack>
          )}

          {/* Step 2: Payment — Stripe PaymentElement + trial summary */}
          {activeStep === 2 && (
            <PaymentStep ref={paymentStepRef} submitting={loading} />
          )}

          {/* Navigation */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column-reverse', sm: 'row' },
              justifyContent: 'space-between',
              gap: 1.5,
              mt: 4,
            }}
          >
            <Button
              variant="outlined"
              size="lg"
              onClick={handleBack}
              disabled={activeStep === 0}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              Back
            </Button>

            {activeStep < steps.length - 1 ? (
              <Button
                size="lg"
                onClick={handleNext}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Next
              </Button>
            ) : (
              <Button
                size="lg"
                onClick={handleSubmit}
                loading={loading}
                disabled={!validateStep(activeStep) || loading}
                loadingIndicator={<CircularProgress size="sm" />}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Start 7-Day Free Trial
              </Button>
            )}
          </Box>

          <Typography level="body-xs" sx={{ mt: 3, textAlign: 'center', color: 'text.tertiary' }}>
            ApexOS is a product of {LEGAL_ENTITY} ·{' '}
            <a
              href="/privacy"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--brand-text, #7013f5)' }}
            >
              Privacy Policy
            </a>{' '}
            ·{' '}
            <a
              href="/terms"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: 'var(--brand-text, #7013f5)' }}
            >
              Terms of Use
            </a>
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};